import { getToken } from "./auth.service";

export const baseUrl = 'https://api.shareguard.me';
// export const baseUrl = 'https://shareguard-ft-api-dev.dominik-390.workers.dev';

export async function getAuthHeaders() {
    const token = await getToken();
    return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
}

async function handleResponse(response) {
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API request failed: ${errorText}`);
    }
    return response.json();
}

export async function apiGet(path) {
    const response = await fetch(`${baseUrl}${path}`, {
        headers: await getAuthHeaders()
    });
    return handleResponse(response);
}

export async function apiPost(path, data) {
    const response = await fetch(`${baseUrl}${path}`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(data)
    });
    return handleResponse(response);
}

export async function apiPut(path, data) {
    const response = await fetch(`${baseUrl}${path}`, {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify(data)
    });
    return handleResponse(response);
}

export async function apiPatch(path, data) {
    const response = await fetch(`${baseUrl}${path}`, {
        method: 'PATCH',
        headers: await getAuthHeaders(),
        body: JSON.stringify(data)
    });
    return handleResponse(response);
}

export async function apiDelete(path) {
    const response = await fetch(`${baseUrl}${path}`, {
        method: 'DELETE',
        headers: await getAuthHeaders()
    });
    return handleResponse(response);
}

export async function apiGetFileContent(path, headers = {}, signal) {
    const authHeaders = await getAuthHeaders();
    
    try {
        const response = await fetch(`${baseUrl}${path}`, {
            headers: {
                ...authHeaders,
                ...headers
            },
            signal: signal
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API request failed: ${errorText}`);
        }
        
        return response;
    } catch (error) {
        // Check if this was an abort error
        if (error.name === 'AbortError') {
            console.log('[API Service] Request was aborted');
            throw new Error('Download was aborted');
        }
        throw error;
    }
}

export async function apiUpload(path, data, headers = {}, progressCallback, method = 'POST', signal) {
    const authHeaders = await getAuthHeaders();
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Handle abort signal
        if (signal) {
            signal.addEventListener('abort', () => {
                xhr.abort();
                reject(new Error('Upload was aborted'));
            });
        }
        
        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable && progressCallback) {
                const progress = (event.loaded / event.total) * 100;
                progressCallback(progress, {
                    loaded: event.loaded,
                    total: event.total
                });
            }
        });
        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response);
                } catch (e) {
                    resolve(xhr.responseText);
                }
            } else {
                console.error('Upload failed:', xhr.statusText);
                reject(new Error(xhr.responseText || `Upload failed: ${xhr.status} ${xhr.statusText}`));
            }
        });
        xhr.addEventListener('error', () => {
            console.error('Network error during upload:', xhr.statusText);
            reject(new Error('Network error during upload'));
        });
        xhr.addEventListener('abort', () => {
            console.log('Upload was aborted');
            reject(new Error('Upload was aborted'));
        });
        xhr.open(method, `${baseUrl}${path}`);
        
        // Don't set Content-Type for multipart/form-data to let browser set it with boundary
        const headersToSet = {
            ...authHeaders,
            ...headers
        };
        if (headersToSet['Content-Type'] === 'multipart/form-data') {
            delete headersToSet['Content-Type'];
        }
        
        Object.entries(headersToSet).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value);
        });
        xhr.send(data);
    });
}

export async function checkHealth() {
    try {
        const response = await fetch(`${baseUrl}/health`, {
            headers: await getAuthHeaders()
        });
        if (!response.ok) {
            throw new Error(`Health check failed: ${response.status}`);
        }
        return response.json();
    } catch (error) {
        console.error('Health check error:', error);
        throw error;
    }
}